/**
 * Sex × age histogram for one or two patient groups.
 *
 * Three views of the same cells:
 *   count   stacked female/male patients per age band
 *   share   the same stack as a share of the group, so two segments of very
 *           different size can be compared on one scale
 *   census  over/under-representation per cell against US adults (1× = the
 *           cell is as common among patients as in the population). Not
 *           stacked: ratios do not add.
 *
 * Two groups render as small multiples on a shared y-axis rather than four
 * colours in one chart; colour stays sex, the panel title carries the segment.
 */

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts'

const SEXES = [
  { key: 'F', label: 'Female', color: 'var(--viz-female)' },
  { key: 'M', label: 'Male', color: 'var(--viz-male)' },
]

const pct = v => (v == null ? '–' : `${(v * 100).toFixed(1)}%`)
const times = v => (v == null ? '–' : `${v.toFixed(2)}×`)
const count = v => Number(v || 0).toLocaleString('en-US')

function rowsFor(group, mode) {
  return group.sexAge.map(cell => {
    const row = { band: cell.band }
    for (const { key } of SEXES) {
      const c = cell[key]
      row[key] = mode === 'count' ? c.patients : mode === 'share' ? c.share : c.index
      row[`${key}_cell`] = c
    }
    return row
  })
}

function TooltipBody({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm">
      <div className="mb-1 font-medium">Age {label}</div>
      {SEXES.map(({ key, label: name, color }) => {
        const c = row[`${key}_cell`]
        return (
          <div key={key} className="flex items-center gap-2 tabular-nums">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: color }} />
            <span className="w-12">{name}</span>
            <span>{count(c.patients)} pts · {pct(c.share)} of patients · {pct(c.censusShare)} of US adults
              {mode === 'census' && <> · <strong>{times(c.index)}</strong></>}</span>
          </div>
        )
      })}
    </div>
  )
}

function Panel({ group, mode, yMax, showTitle }) {
  const data = rowsFor(group, mode)
  const tickFormatter = mode === 'count' ? count : mode === 'share' ? v => `${Math.round(v * 100)}%` : v => `${v}×`
  return (
    <div className="min-w-0 flex-1">
      {showTitle && (
        <div className="mb-2 flex items-baseline gap-2 text-sm">
          <span className="font-medium">{group.label}</span>
          <span className="text-muted-foreground">{count(group.sexAgeKnown)} with sex and age</span>
          {!group.sufficient && <span className="text-xs text-muted-foreground">· thin group</span>}
        </div>
      )}
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="22%">
            <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
            <XAxis dataKey="band" tick={{ fontSize: 12, fill: 'var(--viz-axis)' }} tickLine={false} axisLine={{ stroke: 'var(--viz-stroke)' }} />
            <YAxis
              width={48}
              domain={[0, yMax]}
              tick={{ fontSize: 12, fill: 'var(--viz-axis)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={tickFormatter}
            />
            <Tooltip content={<TooltipBody mode={mode} />} cursor={{ fill: 'var(--viz-grid)', opacity: 0.5 }} />
            {mode === 'census' && (
              <ReferenceLine y={1} stroke="var(--viz-axis)" strokeDasharray="4 3"
                label={{ value: 'US adult mix', position: 'insideTopRight', fontSize: 11, fill: 'var(--viz-axis)' }} />
            )}
            <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            {SEXES.map(({ key, label, color }, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={label}
                fill={color}
                stackId={mode === 'census' ? undefined : 'sex'}
                // Rounded data-end only on the top of the stack.
                radius={mode === 'census' || i === SEXES.length - 1 ? [4, 4, 0, 0] : 0}
                stroke="var(--viz-ring)"
                strokeWidth={mode === 'census' ? 0 : 1}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default function SexAgeChart({ groups, mode }) {
  // One y-domain across panels, or two segments cannot be compared by eye.
  let max = 0
  for (const group of groups) {
    for (const row of rowsFor(group, mode)) {
      const v = mode === 'census' ? Math.max(row.F || 0, row.M || 0) : (row.F || 0) + (row.M || 0)
      if (v > max) max = v
    }
  }
  const yMax = mode === 'census' ? Math.max(1.5, Math.ceil(max * 4) / 4) : mode === 'share' ? Math.min(1, Math.ceil(max * 20) / 20 || 0.05) : Math.max(1, Math.ceil(max * 1.05))

  return (
    <div className="demographics-viz flex flex-col gap-6 lg:flex-row">
      {groups.map(group => (
        <Panel key={group.value ?? 'all'} group={group} mode={mode} yMax={yMax} showTitle={groups.length > 1} />
      ))}
    </div>
  )
}
